import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Gavel as TermsIcon,
  HelpOutline as HelpIcon,
  MenuBook as ManualIcon,
  Security as PrivacyIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  HELP_FAQ_ITEMS,
  TRAINING_GUIDES,
  getTrainingGuidesByCategory,
} from '../constants/helpCenterContent';
import { useRegistration } from '../context/RegistrationContext';
import { UserAccountPanel } from './settings/UserAccountPanel';
import PartnerConsentSettings from './settings/PartnerConsentSettings';
import MarketingHeading from './shared/MarketingHeading';
import { MsmeSupportPanel } from './support/MsmeSupportPanel';

interface PageShellProps {
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

export const PageShell: React.FC<PageShellProps> = ({ icon, title, subtitle, children }) => (
  <Box
    sx={{
      minHeight: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4,
      px: 2,
    }}
  >
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        maxWidth: 900,
        p: { xs: 3, md: 5 },
        borderRadius: 3,
        border: '1px solid rgba(76, 175, 80, 0.12)',
      }}
    >
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon}
          <MarketingHeading level="h1">
            {title}
          </MarketingHeading>
        </Box>
        <Typography color="text.secondary">{subtitle}</Typography>
      </Stack>
      <Divider sx={{ mb: 3 }} />
      {children}
    </Paper>
  </Box>
);

const TrainingGuidesSection: React.FC = () => {
  const location = useLocation();
  const hashGuideId = location.hash?.replace(/^#guide-/, '') || '';
  const guidesByCategory = useMemo(() => getTrainingGuidesByCategory(), []);
  const [expandedGuideId, setExpandedGuideId] = useState<string | false>(
    hashGuideId || TRAINING_GUIDES[0]?.id || false
  );

  useEffect(() => {
    if (hashGuideId) {
      setExpandedGuideId(hashGuideId);
      const element = document.getElementById(`guide-${hashGuideId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [hashGuideId]);

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <ManualIcon color="primary" fontSize="small" />
        <Typography variant="h6">Step-by-step training guides</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Module walkthroughs for onboarding, data import, carbon assessment, compliance, reporting, and finance.
        Each page in the workspace links back to the relevant guide below.
      </Typography>
      {guidesByCategory.map(({ category, label, guides }) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
            {label}
          </Typography>
          <Stack spacing={1}>
            {guides.map((guide) => (
              <Accordion
                key={guide.id}
                id={`guide-${guide.id}`}
                expanded={expandedGuideId === guide.id}
                onChange={(_, isExpanded) => setExpandedGuideId(isExpanded ? guide.id : false)}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:before': { display: 'none' },
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 600 }}>{guide.title}</Typography>
                    <Chip label={label} size="small" variant="outlined" />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {guide.summary}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                    Steps
                  </Typography>
                  <List dense disablePadding sx={{ mb: guide.tips?.length ? 1.5 : 0 }}>
                    {guide.steps.map((step, index) => (
                      <ListItem key={step} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {index + 1}. {step}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  {guide.tips?.length ? (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                        Tips
                      </Typography>
                      <List dense disablePadding sx={{ mb: 1.5 }}>
                        {guide.tips.map((tip) => (
                          <ListItem key={tip} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText primary={<Typography variant="body2">• {tip}</Typography>} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  ) : null}
                  <Button
                    component={RouterLink}
                    to={guide.primaryRoute}
                    variant="outlined"
                    size="small"
                  >
                    {guide.ctaLabel || `Open — ${guide.title}`}
                  </Button>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

export const HelpCenterPage: React.FC = () => {
  const { isRegistered } = useRegistration();

  return (
    <PageShell
      icon={<HelpIcon color="primary" fontSize="large" />}
      title="Help center"
      subtitle="Training guides, FAQs, and AI-assisted support for Sustainow Carbon Intelligence."
    >
      <TrainingGuidesSection />

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Frequently asked questions
      </Typography>
      <List dense sx={{ mb: 2 }}>
        {HELP_FAQ_ITEMS.map((item) => (
          <ListItem key={item.id} alignItems="flex-start">
            <ListItemText primary={item.question} secondary={item.answer} />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <MsmeSupportPanel isRegistered={isRegistered} />

      <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
        Need more help? Contact support at{' '}
        <a href="mailto:contact@sustainow.in">contact@sustainow.in</a>.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button
          component={RouterLink}
          to={isRegistered ? '/dashboard' : '/login'}
          variant="contained"
        >
          {isRegistered ? 'Back to Dashboard' : 'Sign in'}
        </Button>
        <Button component={RouterLink} to="/privacy-policy" variant="outlined">
          Privacy Policy
        </Button>
      </Stack>
    </PageShell>
  );
};

export const PrivacyPolicyPage: React.FC = () => (
  <PageShell
    icon={<PrivacyIcon color="primary" fontSize="large" />}
    title="Privacy Policy"
    subtitle="How the Sustainow Carbon Intelligence solution collects, uses, and protects your data."
  >
    <Typography paragraph>
      Sustainow Carbon Intelligence collects only the information needed to deliver emissions estimation, ESG
      workflows, analytics, and reporting features. This may include account details, operational activity data,
      uploaded documents, and usage telemetry required for secure platform operations.
    </Typography>
    <Typography paragraph>
      Your information is used for authentication, report generation, product support, security monitoring, and
      controlled service improvements. Sensitive information is protected using role-based access controls and
      encryption safeguards in storage and transit.
    </Typography>
    <Typography paragraph>
      Where required, we support correction, deletion, and access requests in line with applicable laws and customer
      agreements. For privacy or data handling requests, contact <strong>contact@sustainow.in</strong>.
    </Typography>

    <Button component={RouterLink} to="/terms-of-service" variant="outlined">
      View Terms of Service
    </Button>
  </PageShell>
);

export const TermsOfServicePage: React.FC = () => (
  <PageShell
    icon={<TermsIcon color="primary" fontSize="large" />}
    title="Terms of Service"
    subtitle="Rules for using the Sustainow Carbon Intelligence solution and related data services."
  >
    <Typography paragraph>
      By accessing or using the platform, you agree to provide accurate data, maintain secure credentials, and use
      the service only for lawful business purposes.
    </Typography>
    <Typography paragraph>
      Analytics, ESG outputs, and recommendations are decision-support tools. You are responsible for validating
      results with your operational context, documentation, and internal approvals before external submission or
      implementation.
    </Typography>
    <Typography paragraph>
      We may restrict or suspend access for misuse, unauthorized activity, data abuse, or security risks. Continued
      use indicates acceptance of ongoing product and policy updates published on the platform.
    </Typography>
    <Typography paragraph>
      For support related to terms, platform usage, or reporting questions, contact <strong>contact@sustainow.in</strong>.
    </Typography>

    <Button component={RouterLink} to="/help" variant="outlined">
      Visit Help center
    </Button>
  </PageShell>
);

type SettingsPageProps = {
  variant?: 'msme' | 'enterprise';
};

export const SettingsPage: React.FC<SettingsPageProps> = ({ variant = 'msme' }) => {
  const isEnterprise = variant === 'enterprise';
  const companyProfilePath = isEnterprise ? '/enterprise/profile' : '/msme-profile';
  const billingPath = isEnterprise ? '/enterprise/pricing' : '/billing';
  const workflowPath = isEnterprise ? '/enterprise/workflow' : '/operations';
  const privacyPath = isEnterprise ? '/enterprise/data-privacy' : '/data-privacy';

  return (
    <PageShell
      icon={<SettingsIcon color="primary" fontSize="large" />}
      title="Settings"
      subtitle="Account details, company profile, privacy, and billing."
    >
      <UserAccountPanel />

      {!isEnterprise && <PartnerConsentSettings />}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Company & platform
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Organization data and compliance workflows are separate from your personal login.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <Button component={RouterLink} to={companyProfilePath} variant="contained">
          {isEnterprise ? 'Organization profile' : 'Company profile'}
        </Button>
        <Button component={RouterLink} to={workflowPath} variant="outlined">
          {isEnterprise ? 'Compliance workflow' : 'Operations setup'}
        </Button>
        {!isEnterprise && (
          <Button component={RouterLink} to="/data?section=connectors" variant="outlined">
            Data connectors
          </Button>
        )}
        <Button component={RouterLink} to={privacyPath} variant="outlined">
          Data privacy
        </Button>
        <Button component={RouterLink} to={billingPath} variant="outlined">
          {isEnterprise ? 'Pricing' : 'Billing'}
        </Button>
        {!isEnterprise && (
          <Button component={RouterLink} to="/information" variant="outlined">
            User manual
          </Button>
        )}
        <Button component={RouterLink} to="/help" variant="outlined">
          Help center
        </Button>
      </Stack>
    </PageShell>
  );
};
